import { useState } from "react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

export default function PercentageCalculator() {

  /* BASIC PERCENTAGE */

  const [value, setValue] =
    useState("");

  const [total, setTotal] =
    useState("");

  const [result, setResult] =
    useState(null);

  /* INCREASE */

  const [oldValue,
    setOldValue] =
    useState("");

  const [newValue,
    setNewValue] =
    useState("");

  const [increaseResult,
    setIncreaseResult] =
    useState(null);

  /* DECREASE */

  const [decOld,
    setDecOld] =
    useState("");

  const [decNew,
    setDecNew] =
    useState("");

  const [decreaseResult,
    setDecreaseResult] =
    useState(null);

  /* DISCOUNT */

  const [price,
    setPrice] =
    useState("");

  const [discount,
    setDiscount] =
    useState("");

  const [discountResult,
    setDiscountResult] =
    useState(null);

  /* CHART */

  const [chartData,
    setChartData] =
    useState([]);

  const COLORS =
    ["#2563eb", "#e5e7eb"];

  /* BASIC */

  const calculatePercentage = () => {

    if (!value || !total) return;

    const res =
      (value / total) * 100;

    setResult(
      res.toFixed(2)
    );

    setChartData([
      {
        name: "Percentage",
        value: Number(res),
      },
      {
        name: "Remaining",
        value: 100 - Number(res),
      },
    ]);
  };

  /* INCREASE */

  const calculateIncrease = () => {

    if (!oldValue || !newValue)
      return;

    const res =
      (
        (
          newValue - oldValue
        ) / oldValue
      ) * 100;

    setIncreaseResult(
      res.toFixed(2)
    );
  };

  /* DECREASE */

  const calculateDecrease = () => {

    if (!decOld || !decNew)
      return;

    const res =
      (
        (
          decOld - decNew
        ) / decOld
      ) * 100;

    setDecreaseResult(
      res.toFixed(2)
    );
  };

  /* DISCOUNT */

  const calculateDiscount = () => {

    if (!price || !discount)
      return;

    const finalPrice =
      price -
      (price * discount) / 100;

    setDiscountResult(
      finalPrice.toFixed(2)
    );
  };

  return (
    <>
      <SEO
        title="Percentage Calculator Online"
        description="Free online percentage calculator with percentage increase, decrease, and discount calculator."
      />

      <Navbar />

      <div className="container py-4">

        {/* HERO */}

        <div className="text-center mb-5">

          <h1 className="fw-bold">
            Percentage Calculator
          </h1>

          <p className="text-muted">
            Calculate percentages,
            percentage increase,
            percentage decrease,
            and discounts instantly.
          </p>

        </div>

        {/* BASIC PERCENTAGE */}

        <div className="card shadow border-0 rounded-4 p-4 mb-5">

          <h2 className="fw-bold mb-4">
            Basic Percentage Calculator
          </h2>

          <div className="row g-3">

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Value"
                className="form-control"
                value={value}
                onChange={(e) =>
                  setValue(e.target.value)
                }
              />

            </div>

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Total"
                className="form-control"
                value={total}
                onChange={(e) =>
                  setTotal(e.target.value)
                }
              />

            </div>

            <div className="col-md-2">

              <button
                className="btn btn-primary w-100"
                onClick={
                  calculatePercentage
                }
              >
                Calculate
              </button>

            </div>

          </div>

          {result && (

            <div className="alert alert-success mt-4">

              <h4>
                Result:
                {" "}
                {result}%
              </h4>

            </div>

          )}

          {result && (

            <div
              style={{
                width: "100%",
                height: 300,
              }}
            >

              <ResponsiveContainer>

                <PieChart>

                  <Pie
                    data={chartData}
                    dataKey="value"
                    outerRadius={90}
                    label
                  >

                    {chartData.map(
                      (
                        entry,
                        index
                      ) => (

                        <Cell
                          key={`cell-${index}`}
                          fill={
                            COLORS[index]
                          }
                        />

                      )
                    )}

                  </Pie>

                  <Tooltip />

                  <Legend />

                </PieChart>

              </ResponsiveContainer>

            </div>

          )}

        </div>

        {/* INCREASE */}

        <div className="card shadow border-0 rounded-4 p-4 mb-5">

          <h2 className="fw-bold mb-4">
            Percentage Increase Calculator
          </h2>

          <div className="row g-3">

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Old Value"
                className="form-control"
                value={oldValue}
                onChange={(e) =>
                  setOldValue(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-5">

              <input
                type="number"
                placeholder="New Value"
                className="form-control"
                value={newValue}
                onChange={(e) =>
                  setNewValue(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-2">

              <button
                className="btn btn-success w-100"
                onClick={
                  calculateIncrease
                }
              >
                Calculate
              </button>

            </div>

          </div>

          {increaseResult && (

            <div className="alert alert-success mt-4">

              <h4>
                Increase:
                {" "}
                {increaseResult}%
              </h4>

            </div>

          )}

        </div>

        {/* DECREASE */}

        <div className="card shadow border-0 rounded-4 p-4 mb-5">

          <h2 className="fw-bold mb-4">
            Percentage Decrease Calculator
          </h2>

          <div className="row g-3">

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Original Value"
                className="form-control"
                value={decOld}
                onChange={(e) =>
                  setDecOld(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Reduced Value"
                className="form-control"
                value={decNew}
                onChange={(e) =>
                  setDecNew(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-2">

              <button
                className="btn btn-warning w-100"
                onClick={
                  calculateDecrease
                }
              >
                Calculate
              </button>

            </div>

          </div>

          {decreaseResult && (

            <div className="alert alert-warning mt-4">

              <h4>
                Decrease:
                {" "}
                {decreaseResult}%
              </h4>

            </div>

          )}

        </div>

        {/* DISCOUNT */}

        <div className="card shadow border-0 rounded-4 p-4 mb-5">

          <h2 className="fw-bold mb-4">
            Discount Calculator
          </h2>

          <div className="row g-3">

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Original Price"
                className="form-control"
                value={price}
                onChange={(e) =>
                  setPrice(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-5">

              <input
                type="number"
                placeholder="Discount %"
                className="form-control"
                value={discount}
                onChange={(e) =>
                  setDiscount(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-2">

              <button
                className="btn btn-danger w-100"
                onClick={
                  calculateDiscount
                }
              >
                Calculate
              </button>

            </div>

          </div>

          {discountResult && (

            <div className="alert alert-danger mt-4">

              <h4>
                Final Price:
                ₹ {discountResult}
              </h4>

            </div>

          )}

        </div>

        {/* SEO CONTENT */}

        <div className="mt-5">

          <h2 className="fw-bold mb-3">
            What is Percentage?
          </h2>

          <p>
            Percentage represents a
            fraction of 100. It is used
            in mathematics, finance,
            shopping discounts, marks,
            and business calculations.
          </p>

          <h2 className="fw-bold mt-5 mb-3">
            Percentage Formula
          </h2>


          <h2 className="fw-bold mt-5 mb-3">
            Percentage Increase Formula
          </h2>


          <h2 className="fw-bold mt-5 mb-3">
            Percentage Decrease Formula
          </h2>


          <h2 className="fw-bold mt-5 mb-3">
            Frequently Asked Questions
          </h2>

          <div className="mb-4">

            <h5>
              How do you calculate percentage?
            </h5>

            <p>
              Divide the value by total
              and multiply by 100.
            </p>

          </div>

          <div className="mb-4">

            <h5>
              How to calculate discount percentage?
            </h5>

            <p>
              Multiply original price
              by discount percentage
              and subtract from price.
            </p>

          </div>

          <div className="mb-4">

            <h5>
              Is this calculator free?
            </h5>

            <p>
              Yes, this online percentage
              calculator is completely free.
            </p>

          </div>

        </div>

      </div>

      <Footer />
    </>
  );
}